import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarClock, CheckCircle2, RotateCcw } from 'lucide-react';
import {
  fetchTenantAppointment,
  respondToTenantAppointment,
} from '../api/tenantPortal';
import { formatDateTime, formatStatus } from '../lib/formatters';
import { useSubmissionLock } from '../hooks/useSubmissionLock';
import { createIdempotencyKey } from '../lib/idempotency';

export default function TenantAppointmentResponse() {
  const { token } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState('');
  const { run: runSubmission, isPending } = useSubmissionLock();

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');

    fetchTenantAppointment(token)
      .then((data) => {
        if (mounted) setJob(data);
      })
      .catch((err) => {
        console.error(err);
        if (mounted) setError('This appointment link is invalid or has expired.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  const respond = async (action) => {
    if (busy || isPending('tenant-appointment-response')) return;
    if (action === 'request_reschedule' && !notes.trim()) {
      setError('Please add a time that works better.');
      return;
    }

    await runSubmission('tenant-appointment-response', async () => {
      try {
        setBusy(true);
        setError('');
        const updated = await respondToTenantAppointment(token, {
          action,
          notes: notes.trim(),
          idempotencyKey: createIdempotencyKey(`tenant-appointment-${action}`),
        });
        setJob(updated);
        setDone(action === 'confirm' ? 'Appointment confirmed.' : 'Request sent.');
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.error || 'Unable to update this appointment right now.');
      } finally {
        setBusy(false);
      }
    });
  };

  if (loading) {
    return <div className="min-h-screen p-8 text-white">Loading appointment...</div>;
  }

  if (error && !job) {
    return <div className="min-h-screen p-8 text-red-300">{error}</div>;
  }

  const proposed = job?.schedule?.scheduledStartAt || job?.schedule?.proposedDate;
  const readOnly = done || job?.schedule?.status === 'CONFIRMED';

  return (
    <div className="min-h-screen p-6 md:p-10 text-white">
      <main className="max-w-2xl mx-auto bg-white/10 border border-white/10 rounded-xl p-6 backdrop-blur-md">
        <img
          src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
          alt="Beem"
          className="w-24 mb-6"
        />

        <div className="flex items-center gap-3 text-[#AF9B8A] mb-3">
          <CalendarClock className="w-5 h-5" />
          <span className="text-sm uppercase tracking-wide">Maintenance appointment</span>
        </div>

        <h1 className="text-3xl mb-3">{job?.title || 'Maintenance appointment'}</h1>
        <p className="text-white/70 mb-6">{job?.description || 'A contractor has proposed an appointment.'}</p>

        <div className="space-y-3 text-sm text-white/80 mb-6">
          <p><strong>Property:</strong> {job?.property?.address || job?.property?.title || 'Property'}</p>
          <p><strong>Contractor:</strong> {job?.contractor?.name || 'Contractor'}</p>
          <p><strong>Proposed time:</strong> {formatDateTime(proposed, 'Not set')}</p>
          <p><strong>Status:</strong> {formatStatus(job?.schedule?.status || 'PROPOSED')}</p>
        </div>

        {error && <p className="text-red-300 mb-4">{error}</p>}
        {done && (
          <div className="flex items-center gap-2 text-emerald-200 bg-emerald-500/10 border border-emerald-400/20 rounded-lg p-3 mb-4">
            <CheckCircle2 className="w-5 h-5" />
            {done}
          </div>
        )}

        {!readOnly && (
          <div className="space-y-4">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="If this time does not work, suggest another time"
              rows="3"
              className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-white"
            />

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => respond('confirm')}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#AF9B8A] text-white disabled:opacity-50"
                aria-busy={busy}
              >
                <CheckCircle2 className="w-4 h-4" />
                {busy ? 'Confirming...' : 'This works'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => respond('request_reschedule')}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/10 disabled:opacity-50"
                aria-busy={busy}
              >
                <RotateCcw className="w-4 h-4" />
                {busy ? 'Submitting...' : 'Request another time'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
